import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLocaleContext } from '../context/LocaleContext';
import knowledgeMetadata from '../data/knowledgeMetadata.json';
import { getKnowledgeArticleUrlSync } from '../knowledge/slugMapping';
import { BookOpen, ArrowRight, Lightbulb } from 'lucide-react';

export const KnowledgeCenter: React.FC = () => {
  const { locale } = useLocaleContext();

  // Define the 4 featured knowledge articles
  const featuredSlugs = [
    'what-are-ai-watermarks',
    'watermark-removal-vs-ai-detection',
    'token-distribution-ai-watermarking',
    'why-ai-detectors-fail'
  ];

  // Get article metadata for each featured article from static metadata
  const featuredArticles = useMemo(() => {
    const articles = knowledgeMetadata[locale as keyof typeof knowledgeMetadata] || knowledgeMetadata.en;
    return featuredSlugs
      .map(slug => articles.find(article => article.slug === slug))
      .filter(article => article !== undefined);
  }, [locale]);

  // Helper function to get the "View All" knowledge center URL
  const getKnowledgeCenterUrl = (): string => {
    if (locale === 'en') {
      return '/knowledge';
    } else if (locale === 'de') {
      return '/de/wissen';
    } else if (locale === 'pt-BR') {
      return '/pt-br/conhecimento';
    } else if (locale === 'ms-MY') {
      return '/my/pengetahuan';
    } else if (locale === 'es') {
      return '/es/conocimiento';
    } else if (locale === 'fr') {
      return '/fr/connaissances';
    } else if (locale === 'ja') {
      return '/ja/chishiki';
    } else if (locale === 'id') {
      return '/id/pengetahuan-ai';
    }
    // Fallback to English
    return '/knowledge';
  };

  // Get localized text
  const getText = (key: string): string => {
    const translations: Record<string, Record<string, string>> = {
      badge: {
        en: 'Knowledge Center',
        de: 'Wissenszentrum',
        'pt-BR': 'Centro de Conhecimento',
        'ms-MY': 'Pusat Pengetahuan',
        es: 'Centro de Conocimiento',
        fr: 'Base de connaissances',
        ja: 'ナレッジセンター',
        id: 'Pusat Pengetahuan'
      },
      sectionTitle: {
        en: 'Knowledge Center',
        de: 'Wissenszentrum',
        'pt-BR': 'Centro de Conhecimento',
        'ms-MY': 'Pusat Pengetahuan',
        es: 'Centro de Conocimiento',
        fr: 'Base de connaissances',
        ja: 'ナレッジセンター',
        id: 'Pusat Pengetahuan'
      },
      sectionDescription: {
        en: 'Explore technical explanations of AI watermarking, detection methods, and removal techniques. Each article covers mechanisms, applications, and limitations.',
        de: 'Entdecken Sie technische Erklärungen zu KI-Wasserzeichen, Erkennungsmethoden und Entfernungstechniken. Jeder Artikel behandelt Mechanismen, Anwendungen und Einschränkungen.',
        'pt-BR': 'Explore explicações técnicas sobre marcas d\'água de IA, métodos de detecção e técnicas de remoção. Cada artigo aborda mecanismos, aplicações e limitações.',
        'ms-MY': 'Terokai penjelasan teknikal tentang tanda air AI, kaedah pengesanan, dan teknik penyingkiran. Setiap artikel merangkumi mekanisme, aplikasi, dan batasan.',
        es: 'Explora explicaciones técnicas sobre marcas de agua de IA, métodos de detección y técnicas de eliminación. Cada artículo cubre mecanismos, aplicaciones y limitaciones.',
        fr: 'Decouvrez des explications techniques sur les filigranes IA, les methodes de detection et les techniques de suppression. Chaque article presente les mecanismes, applications et limites.',
        ja: 'AI透かしの仕組み、検出方法、削除技術について詳しく解説しています。各記事ではメカニズム、活用例、限界について説明しています。',
        id: 'Jelajahi penjelasan teknis tentang watermark AI, metode deteksi, dan teknik penghapusan. Setiap artikel membahas mekanisme, aplikasi, dan keterbatasan.'
      },
      readArticle: {
        en: 'Read article',
        de: 'Artikel lesen',
        'pt-BR': 'Ler artigo',
        'ms-MY': 'Baca artikel',
        es: 'Leer artículo',
        fr: 'Lire l\'article',
        ja: '記事を読む',
        id: 'Baca artikel'
      },
      viewAll: {
        en: 'View all articles',
        de: 'Alle Artikel ansehen',
        'pt-BR': 'Ver todos os artigos',
        'ms-MY': 'Lihat semua artikel',
        es: 'Ver todos los artículos',
        fr: 'Voir tous les articles',
        ja: '全ての記事を見る',
        id: 'Lihat semua artikel'
      }
    };

    return translations[key]?.[locale] || translations[key]?.['en'] || '';
  };

  if (featuredArticles.length === 0) {
    return null;
  }

  return (
    <div className="mt-20 mb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-100 dark:bg-accent-900/30 text-accent-700 dark:text-accent-300 rounded-full mb-4">
            <Lightbulb className="w-5 h-5" />
            <span className="font-semibold">
              {locale === 'de'
                ? 'Mehr erfahren'
                : locale === 'pt-BR'
                ? 'Saiba Mais'
                : locale === 'ms-MY'
                ? 'Ketahui Lebih'
                : locale === 'es'
                ? 'Aprende Mas'
                : locale === 'fr'
                ? 'En savoir plus'
                : locale === 'ja'
                ? '詳しく見る'
                : locale === 'id'
                ? 'Pelajari Lebih'
                : 'Learn More'}
            </span>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-charcoal-900 dark:text-white mb-4">
            {getText('sectionTitle')}
          </h2>

          <p className="text-lg text-neutral-600 dark:text-neutral-300 max-w-3xl mx-auto">
            {getText('sectionDescription')}
          </p>
        </div>

        {/* Knowledge Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {featuredArticles.map((article) => {
            const articleUrl = getKnowledgeArticleUrlSync(article.slug, locale);

            return (
              <Link
                key={article.slug}
                to={articleUrl}
                className="group bg-white dark:bg-charcoal-800 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-neutral-200 dark:border-charcoal-700 hover:border-accent-500 dark:hover:border-accent-500 hover:-translate-y-1"
              >
                <div className="p-6">
                  {/* Icon and Badge */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-accent-100 dark:bg-accent-900/30 rounded-lg">
                      <BookOpen className="w-5 h-5 text-accent-600 dark:text-accent-400" aria-hidden="true" />
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-charcoal-900 dark:text-white mb-3 group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors line-clamp-2">
                    {article.title}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-4 line-clamp-3">
                    {article.metaDescription}
                  </p>

                  {/* Read More - Unique per article for SEO */}
                  <span className="inline-flex items-center gap-2 text-accent-600 dark:text-accent-300 font-semibold text-sm group-hover:gap-3 transition-all">
                    {getText('readArticle')}: {article.title.length > 25 ? article.title.substring(0, 25) + '...' : article.title}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>

        {/* View All Button */}
        <div className="text-center">
          <Link
            to={getKnowledgeCenterUrl()}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-accent-600 to-accent-700 hover:from-accent-700 hover:to-accent-800 text-white font-semibold rounded-lg transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
          >
            {getText('viewAll')}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeCenter;
